import CloudDownload from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import { FieldConfiguration } from '../../../api/ConfigApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import MediaApi from '../../../api/MediaApi';
import { AuthContext, AuthData } from '../../../context/auth';
import { MediaResponse } from '../../../types/media';
import { FieldValues } from '../../../types/policy';
import { errorToast } from '../../Toast';

const useStyles = makeStyles((theme) => ({
	input: {
		display: 'none',
	},
}));

interface Props {
	labelId: string;
	authData: AuthData;
	value: FieldValues;
	field: FieldConfiguration;
	mediaByLocator: { [locator: string]: MediaResponse };
	disabled: boolean;
	updateMedia: (val: string[]) => void;
	required: boolean;
}

const MediaField = (props: Props) => {
	const classes = useStyles();

	const {
		labelId,
		field,
		authData,
		mediaByLocator,
		value,
		disabled,
		updateMedia,
		required,
	} = props;
	const { hasPermission } = authData;
	const nonNullValues = value?.filter((v) => !!v) || [];
	const intialFileLocators = mediaByLocator
		? Object.keys(mediaByLocator)
		: nonNullValues || [];
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [fileLocators, setFileLocators] =
		useState<string[]>(intialFileLocators);
	let initialFiles: MediaResponse[] = [];

	useEffect(() => {
		const load = async () => {
			if (nonNullValues.length) {
				if (mediaByLocator) {
					initialFiles = await Promise.all(
						nonNullValues.map(async (locator: string) => {
							if (mediaByLocator[locator]) {
								return mediaByLocator[locator];
							} else {
								const response = await MediaApi.getFile(
									authData.auth.authorizationToken,
									locator,
								);
								return response.data;
							}
						}),
					);
					setFiles(initialFiles);
				} else {
					try {
						initialFiles = await Promise.all(
							nonNullValues.map(async (locator: string) => {
								const response = await MediaApi.getFile(
									authData.auth.authorizationToken,
									locator,
								);
								return response.data;
							}),
						);
					} catch (error) {
						if (await handleUnauthorizedResponse(error, authData)) {
							return;
						}
					}

					setFiles(initialFiles);
				}
			}
		};

		load();
	}, [mediaByLocator, value]);

	const selectFile = (e: React.FormEvent) => {
		const element = e.currentTarget as HTMLInputElement;
		const file = element.files[0];
		if (file) {
			uploadFile(file);
		}
	};

	const uploadFile = async (file: File) => {
		try {
			setLoading(true);
			const res = await MediaApi.uploadFile(
				authData.auth.authorizationToken,
				file,
			);
			const addedFileLocator = res.data.locator;
			const fileDetails = await MediaApi.getFile(
				authData.auth.authorizationToken,
				addedFileLocator,
			);
			const newLocators = fileLocators.concat(addedFileLocator);
			updateMedia(newLocators);
			setFileLocators(newLocators);
			setFiles(files.concat(fileDetails.data));
			setLoading(false);
		} catch (error) {
			setLoading(false);
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to upload media');
		}
	};

	const removeFile = (locator: string) => {
		const newLocators = fileLocators.filter((l) => l !== locator);
		const newFiles = files.filter((f) => f.locator !== locator);
		updateMedia(newLocators);
		setFileLocators(newLocators);
		setFiles(newFiles);
	};

	const renderFiles = () => {
		return files.map((f) => {
			return (
				<div key={f.locator} style={{ display: 'flex', alignItems: 'center' }}>
					<IconButton href={f.url} size='small'>
						<CloudDownload />
					</IconButton>
					{!disabled && (
						<IconButton size='small' onClick={() => removeFile(f.locator)}>
							<DeleteIcon />
						</IconButton>
					)}
					{f.fileName}
				</div>
			);
		});
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			<FormLabel id={labelId} required={required}>
				{field.title || ''}
			</FormLabel>
			{!disabled && !loading && (
				<>
					<input
						accept='*'
						className={classes.input}
						id={`${labelId}-media`}
						type='file'
						onChange={(e) => selectFile(e)}
					/>
					<label htmlFor={`${labelId}-media`} style={{ marginRight: 'auto' }}>
						{hasPermission('media.upload') && (
							<Button
								variant='contained'
								color='primary'
								style={{
									backgroundColor: '#02457A',
									color: '#FFFFFF',
									margin: '5px',
									height: '32px',
								}}
								component='span'
								startIcon={<CloudUploadIcon style={{ paddingRight: '3px' }} />}
							>
								Upload
							</Button>
						)}
					</label>
				</>
			)}
			{loading && (
				<Button
					variant='contained'
					color='primary'
					disabled
					style={{ margin: '5px auto 5px 5px', width: '105px', height: '32px' }}
					component='span'
				>
					<CircularProgress size={15} />
				</Button>
			)}
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{renderFiles()}
			</div>
		</div>
	);
};

function withContext(Component: any) {
	return (props: Partial<Props>) => (
		<AuthContext.Consumer>
			{(authData) => <Component {...props} authData={authData} />}
		</AuthContext.Consumer>
	);
}

export default withContext(MediaField);
